import {
    BaseServiceDao,
    type IEditableDataSource,
    type IPaginatedResult,
    parseCollection,
    parseMultiLanguageText,
    parsePaginatedResult,
} from "@/data/dao/service/BaseServiceDao";
import type {
    IAbsoluteDiscountOptions,
    IDiscount,
    IDiscountAccess,
    IDiscountApplicability,
    IDiscountApplicableCollections,
    IDiscountUserGroupAccessSettings,
    IPercentageDiscountOptions,
} from "@/data/model/Discount";
import type { ILocationPaymentMethod } from "@/data/model/LocationPaymentMethod";
import type { ILinkableEntity, IMultiLanguageLinkableEntity } from "@/data/model/Shared";
import type {
    ICheckCustomDiscountCodeCmd,
    ICheckCustomDiscountCodeResult,
} from "@/data/queries/discounts/ICheckCustomDiscountCodeCmd";
import type { ICreateDiscountCmd } from "@/data/queries/discounts/ICreateDiscountCmd";
import type { IDiscountQry } from "@/data/queries/discounts/IDiscountQry";
import type { IGenerateDiscountCodeResult } from "@/data/queries/discounts/IGenerateDiscountCodeCmd";
import type { IUpdateDiscountCmd } from "@/data/queries/discounts/IUpdateDiscountCmd";
import { DateTime } from "@/data/util/DateTime";

import { CatalogDao } from "./CatalogDao";
import { LocationDao } from "./LocationDao";

export class DiscountsDao extends BaseServiceDao implements IEditableDataSource<IDiscount> {
    public static className = "DiscountsDao";

    constructor() {
        super("discounts");
        this.isLocationDependent = true;
    }

    public async getSingle(id: string): Promise<IDiscount> {
        return this.get(id, undefined, DiscountsDao.parseDiscount);
    }

    public async getData(qry: IDiscountQry): Promise<IPaginatedResult<IDiscount>> {
        return this.get("", qry, (f) => parsePaginatedResult(f, DiscountsDao.parseDiscount));
    }

    public async create(cmd: ICreateDiscountCmd): Promise<IDiscount> {
        return this.post("", cmd, DiscountsDao.parseDiscount);
    }

    public async update(id: string, cmd: IUpdateDiscountCmd): Promise<IDiscount> {
        return this.put(id, cmd, DiscountsDao.parseDiscount);
    }

    public async cancel(id: string): Promise<IDiscount> {
        return this.put(id + "/cancel", {}, DiscountsDao.parseDiscount);
    }

    public async remove(id: string): Promise<void> {
        return this.delete(id, {});
    }

    public async generateDiscountCode(): Promise<IGenerateDiscountCodeResult> {
        return this.post("generate-code", {}, DiscountsDao.parseGenerateDiscountCodeResponse);
    }

    public async checkCustomDiscountCode(cmd: ICheckCustomDiscountCodeCmd): Promise<ICheckCustomDiscountCodeResult> {
        return this.post("check-custom-code", cmd, DiscountsDao.parseCheckCustomDiscountCodeResult);
    }

    public async getApplicableCollections(): Promise<IDiscountApplicableCollections> {
        return this.get("applicable-collections", {}, DiscountsDao.parseApplicableCollections);
    }

    public async getDiscountPaymentMethods(): Promise<Array<ILocationPaymentMethod>> {
        return this.get("payment-methods", {}, (c) => parseCollection(c, LocationDao.parseLocationPaymentMethod));
    }

    public static parseDiscount(literal: any): IDiscount {
        return {
            id: literal.id,
            locationId: literal.locationId,
            type: parseInt(literal.type),
            name: parseMultiLanguageText(literal.name),
            internalName: literal.internalName == null ? undefined : literal.internalName,
            code: literal.code,
            customCode: literal.customCode == null ? undefined : literal.customCode,
            order: literal.order == null ? undefined : literal.order,
            createdDateTime: DateTime.parseIsoDate(literal.createdDateTime),
            updatedDateTime: DateTime.parseIsoDate(literal.updatedDateTime),
            isCancelled: literal.isCancelled,
            cancellationDateTime:
                literal.cancellationDateTime == null ? undefined : DateTime.parseIsoDate(literal.cancellationDateTime),
            absoluteDiscountOptions: literal.absoluteDiscountOptions
                ? DiscountsDao.parseAbsoluteDiscountOptions(literal.absoluteDiscountOptions)
                : undefined,
            percentageDiscountOptions: literal.percentageDiscountOptions
                ? DiscountsDao.parsePercentageDiscountOptions(literal.percentageDiscountOptions)
                : undefined,
            applicability: DiscountsDao.parseDiscountApplicability(literal.applicability),
            userGroupAccessSettings: DiscountsDao.parseUserGroupAccessSettings(literal.userGroupAccessSettings),
            accountingPaymentMethodId: literal.accountingPaymentMethodId,
            status: parseInt(literal.status),
            timesUsed: literal.timesUsed,
            totalDiscounted: literal.totalDiscounted,
            isAlreadyUsed: literal.isAlreadyUsed,
        };
    }

    public static parseAbsoluteDiscountOptions(literal: any): IAbsoluteDiscountOptions {
        return {
            value: parseFloat(literal.value),
            applyOncePerOrder: literal.applyOncePerOrder,
        };
    }

    public static parsePercentageDiscountOptions(literal: any): IPercentageDiscountOptions {
        return {
            value: parseFloat(literal.value),
        };
    }

    public static parseDiscountApplicability(literal: any): IDiscountApplicability {
        return {
            allApplications: literal.allApplications,
            allOffers: literal.allOffers,
            allMenuCategories: literal.allMenuCategories,
            allArticles: literal.allArticles,
            applicableOrderUseCaseIds: literal.applicableOrderUseCaseIds ?? [],
            applicablePayUseCaseIds: literal.applicablePayUseCaseIds ?? [],
            applicableOfferIds: literal.applicableOfferIds ?? [],
            applicableMenuCategoryIds: literal.applicableMenuCategoryIds ?? [],
            applicableArticleIds: literal.applicableArticleIds ?? [],
            startDateTime: DateTime.parseIsoDate(literal.startDateTime),
            endDateTime: DateTime.parseIsoDate(literal.endDateTime),
        };
    }

    public static parseUserGroupAccessSettings(literal: any): IDiscountUserGroupAccessSettings {
        return {
            operators: DiscountsDao.parseAccess(literal.operators),
            leadOperators: DiscountsDao.parseAccess(literal.leadOperators),
            endUsers: DiscountsDao.parseAccess(literal.endUsers),
        };
    }

    public static parseAccess(literal: any): IDiscountAccess {
        return {
            enabled: literal.enabled,
            mode: literal.mode == null ? undefined : literal.mode,
        };
    }

    public static parseGenerateDiscountCodeResponse(literal: any): IGenerateDiscountCodeResult {
        return {
            discountCode: literal.discountCode,
        };
    }

    public static parseCheckCustomDiscountCodeResult(literal: any): ICheckCustomDiscountCodeResult {
        return {
            isAvailable: literal.isAvailable,
        };
    }

    public static parseApplicableCollections(literal: any): IDiscountApplicableCollections {
        return {
            orderUseCases: parseCollection(literal.orderUseCases, DiscountsDao.parseMultiLinkableEntity),
            payUseCases: parseCollection(literal.payUseCases, DiscountsDao.parseMultiLinkableEntity),
            offers: parseCollection(literal.offers, DiscountsDao.parseLinkableEntity),
            offerMenuCategories: new Map(Object.entries(literal.offerMenuCategories ?? {})),
            menuCategories: parseCollection(literal.menuCategories, DiscountsDao.parseMultiLinkableEntity),
            menuCategoryArticles: new Map(Object.entries(literal.menuCategoryArticles ?? {})),
            articles: parseCollection(literal.articles, DiscountsDao.parseMultiLinkableEntity),
            options: parseCollection(literal.options, DiscountsDao.parseMultiLinkableEntity),
            articleOptions: new Map(Object.entries(literal.articleOptions ?? {})),
        };
    }

    public static parseMultiLinkableEntity(literal: any): IMultiLanguageLinkableEntity {
        return {
            id: literal.id,
            name: literal.name,
            internalName: literal.internalName == null ? undefined : literal.internalName,
            appearsIn:
                literal.appearsIn != null
                    ? parseCollection(literal.appearsIn, CatalogDao.parseLinkableEntity)
                    : undefined,
            order: literal.order == null ? undefined : literal.order,
        };
    }

    public static parseLinkableEntity(literal: any): ILinkableEntity {
        return {
            id: literal.id,
            name: literal.name,
            appearsIn:
                literal.appearsIn != null
                    ? parseCollection(literal.appearsIn, CatalogDao.parseLinkableEntity)
                    : undefined,
        };
    }
}
